import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import ZoomCross24PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ZoomCross24PxOutlined";
import { Thumbnails } from "../../Thumbnails/Thumbnails";
import styles from "./ModalContent.module.scss";
import { Navigation } from "../../Navigation";
import { NumberIndicator } from "../../NumberIndicator";
import { LineIndicator } from "../../LineIndicator/LineIndicator";
import { ImageElement, ImageElementRef } from "./ImageElement";
import { GalleryVideoElement } from "../../GalleryVideoElement/GalleryVideoElement";
import { useDrag } from "@use-gesture/react";
import classNames from "classnames/bind";
import { ZoomTooltip } from "../../ZoomTooltip/ZoomTooltip";
import { GalleryImageObject, GalleryVideoObject } from "../../../utils";
import { dispatchGalleryMonetateQEvent } from "../../Analytics/dispatchGalleryMonetateQEvent";

const TOOLTIP_FADEOUT_TIME = 8000;

const cx = classNames.bind(styles);

export interface ModalContentProps {
  mode?: "image" | "video";
  images: GalleryImageObject[];
  videos?: GalleryVideoObject[];
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  selectedIndex: number;
}

export const ModalContent = ({
  mode = "image",
  images,
  videos,
  ...props
}: ModalContentProps) => {
  const [selectedGalleryObject, setSelectedGalleryObject] = useState(
    mode === "image" ? props.selectedIndex : 0,
  );

  const imagesRef = useRef<Array<ImageElementRef | null>>([]);
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragLength, setDragLength] = useState(0);
  const [isTooltipHidden, setIsTooltipHidden] = useState(false);
  const [modalHasInteraction, setModalHasInteraction] = useState(false);
  const firstRender = useRef(true);

  const numberOfGalleryObjects =
    mode === "image" ? images.length : videos!.length;

  useEffect(() => {
    const handler = (e: Event) => e.preventDefault();
    document.addEventListener("gesturestart", handler);
    document.addEventListener("gesturechange", handler);
    document.addEventListener("gestureend", handler);
    return () => {
      document.removeEventListener("gesturestart", handler);
      document.removeEventListener("gesturechange", handler);
      document.removeEventListener("gestureend", handler);
    };
  }, []);

  useEffect(() => {
    const fadeOut = () => {
      setIsTooltipHidden(true);
    };

    const timeout = window.setTimeout(fadeOut, TOOLTIP_FADEOUT_TIME);

    return () => {
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (mode === "image" && !firstRender.current) {
      props.setSelectedIndex(
        selectedGalleryObject > 0
          ? selectedGalleryObject + (videos?.length ?? 0)
          : selectedGalleryObject,
      );
      imagesRef.current.map((imageEl) => {
        imageEl!.reset();
      });
    }

    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setIsTooltipHidden(true);
  }, [modalHasInteraction, selectedGalleryObject]);

  useDrag(
    ({ cancel, movement: [movementX], active }) => {
      const currentImageScale =
        imagesRef.current[selectedGalleryObject]?.getScale() || 1;

      if (currentImageScale === 1 && active) {
        setModalHasInteraction(true);

        const percentMoved = (movementX / window.innerWidth) * 100;
        setDragLength(percentMoved);

        if (Math.abs(percentMoved) > 30) {
          const change = movementX > 0 ? -1 : 1;

          if (change > 0) {
            dispatchGalleryMonetateQEvent("SwipeLeft");
          } else {
            dispatchGalleryMonetateQEvent("SwipeRight");
          }

          setSelectedGalleryObject((currentIndex) => {
            return Math.min(
              Math.min(
                Math.max(0, currentIndex + change),
                numberOfGalleryObjects - 1,
              ),
            );
          });
          cancel();
        } else {
          setDragLength(0);
        }
      } else {
        setDragLength(0);
      }
    },
    { target: trackRef },
  );

  const translateStyle = {
    transform: `translateX(${selectedGalleryObject * -100 + dragLength}%)`,
  };

  const [isZoomLabelActive, setZoomLabelActive] = useState(false);

  const [zoomLabelPosition, setZoomLabelPosition] = useState({
    top: "0",
    left: "0",
  });

  const handleMouseMove = (event: React.MouseEvent<HTMLImageElement>) => {
    const {
      nativeEvent: { clientX, clientY },
    } = event;

    const element = document.elementFromPoint(clientX, clientY);

    if (
      isZoomLabelActive &&
      element?.getAttribute("data-testid") === "gallery:zoom:modal:image"
    ) {
      setZoomLabelPosition({
        top: `${clientY - window.innerHeight * 0.35 - 22}px`,
        left: `${clientX - window.innerWidth / 2 - 22}px`,
      });
    }
  };

  const handleFocus = () => {
    const elem = document.querySelectorAll(`${styles.contentWrapper} img`)[
      selectedGalleryObject
    ];
    if (elem) {
      const rect = elem.getBoundingClientRect();

      setZoomLabelPosition({
        top: `${rect.y + rect.height / 2 - 30}px`,
        left: `${rect.x + rect.width / 2 - 60}px`,
      });

      setZoomLabelActive(true);
    }
  };

  const handleMouseEnter = () => {
    setZoomLabelActive(true);
  };

  const handleMouseLeave = () => {
    setZoomLabelActive(false);
  };

  return (
    <div
      data-testid="gallery:zoom:modal"
      className={styles.contentWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
    >
      {mode === "image" ? (
        <>
          <div
            className={cx(styles.zoomTip, {
              fadeOut: isTooltipHidden,
            })}
            data-testid="gallery:zoom:modal:tip"
          >
            <div className={styles.zoomIcon}>
              <ZoomCross24PxOutlinedIcon />
            </div>
            <span className={styles.zoomText}>Scale and pinch</span>
            <span className={styles.desktopZoomText}>
              <ZoomTooltip
                toolTipText="Click to zoom"
                showIconOnly={false}
                zoomLabelPosition={zoomLabelPosition}
              ></ZoomTooltip>
            </span>
          </div>
          <div
            className={styles.track}
            style={translateStyle}
            ref={trackRef}
            data-testid="gallery:zoom:modal:track"
          >
            {images.map((image, idx) => {
              return (
                <ImageElement
                  image={image}
                  key={image.imageUrl}
                  ref={(el) => (imagesRef.current[idx] = el)}
                  setModalHasInteraction={setModalHasInteraction}
                  isSelectedIndex={idx === selectedGalleryObject}
                />
              );
            })}
          </div>
        </>
      ) : (
        <div
          className={styles.track}
          style={translateStyle}
          ref={trackRef}
          data-testid="gallery:zoom:modal:track"
        >
          {videos!.map((video, idx) => {
            return (
              <GalleryVideoElement
                video={video}
                videoId={idx.toString()}
                isModal={true}
                key={`video-${idx}`}
                isInView={selectedGalleryObject === idx}
              />
            );
          })}
        </div>
      )}
      <Navigation
        inModal
        mode={mode}
        numberOfImages={numberOfGalleryObjects}
        selectedIndex={selectedGalleryObject}
        setSelectedIndex={setSelectedGalleryObject}
      />
      <div className={styles.thumbnailsContainer}>
        <Thumbnails
          inModal
          mode={mode}
          galleryObjects={mode === "image" ? images : videos!}
          setSelectedIndex={setSelectedGalleryObject}
          selectedIndex={selectedGalleryObject}
        />
      </div>
      <div className={styles.indicatorsWrapper}>
        {numberOfGalleryObjects > 1 && (
          <LineIndicator
            currentIndex={selectedGalleryObject}
            numberOfImages={numberOfGalleryObjects}
          />
        )}
        <NumberIndicator
          currentIndex={selectedGalleryObject}
          numberOfImages={numberOfGalleryObjects}
        />
      </div>
    </div>
  );
};
